import { CloseOutlined, ExpandAltOutlined } from '@ant-design/icons'
import { Button, Popover, Tooltip } from 'antd'
import { t } from 'i18next'
import { type ReactElement, type ReactNode, useEffect } from 'react'

import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'

interface Properties {
  text: string
  columnWidth: number
  children: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * セルが active（cursor が当たっている）の時に true。
   * MainCursorBox（zIndex 10/21）が icon に重なって hover を奪うため、
   * この時は hover に依らず icon を可視にし、icon の zIndex を MainCursorBox より上げる。
   */
  forceVisible?: boolean
}

/**
 * セル内長文の全文を独立 Popover で閲覧するためのトリガー。
 *
 * 振る舞い:
 * - children をラップし、セル hover 時にセル右下に展開アイコンを表示（CSS only）
 * - アイコンクリックで Popover の開閉を親へ通知（fully controlled）
 * - 他セルとの mutual exclusion / unmount 時の close / 列幅変更時の close は呼び出し元（Redux 側）が担当する
 */
export const CellLongTextViewerTrigger = ({
  text,
  columnWidth,
  children,
  open,
  onOpenChange,
  forceVisible = false,
}: Properties): ReactElement => {
  // open 中のみ Esc キーをリスン（outside click では close しない: 他セルをいじりながら閲覧したいため）
  useEffect(() => {
    if (!open) {
      return
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onOpenChange(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return (): void => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onOpenChange])

  const showTrigger = text !== ''
  // active の時は MainCursorBox（zIndex 10/21）が icon の上に重なるため、
  // hover に依らず常時可視 + zIndex を MainCursorBox より上に上げる
  const triggerVisibilityClassName = forceVisible
    ? 'absolute bottom-0 right-0 cursor-pointer'
    : 'absolute bottom-0 right-0 cursor-pointer opacity-0 group-hover:opacity-100'
  const triggerZIndex = forceVisible
    ? RSheetsStyle.zIndex.cellOverlayTriggerWhenActive
    : RSheetsStyle.zIndex.cellOverlayTrigger

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={[]}
      arrow={false}
      align={{ points: ['tl', 'tl'] }}
      styles={{
        body: {
          width: columnWidth,
          minWidth: columnWidth,
          padding: '8px 12px',
        },
      }}
      content={
        // Popover content は portal だが React 合成イベントは React tree に沿って bubble するため、
        // セル側 (MainCursorBox) の pointerdown(ドラッグ開始) / click(編集モード遷移) / contextmenu(右クリックメニュー) を止める
        <div
          className="relative"
          onPointerDown={(event) => {
            event.stopPropagation()
          }}
          onClick={(event) => {
            event.stopPropagation()
          }}
          onContextMenu={(event) => {
            event.stopPropagation()
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined className="text-xs text-gray-500" />}
            aria-label={t('閉じる')}
            data-testid="cell-long-text-viewer-close"
            onClick={() => {
              onOpenChange(false)
            }}
            className="absolute -right-2 -top-1 z-[1] h-auto p-0.5 leading-none"
          />
          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words pr-4">{text}</div>
        </div>
      }
    >
      <div className="group size-full">
        {children}
        {showTrigger ? (
          <Tooltip title={t('全文を固定表示')}>
            <div
              role="button"
              tabIndex={0}
              aria-label={t('全文を固定表示')}
              data-testid="cell-long-text-viewer-trigger"
              className={`${triggerVisibilityClassName} p-0.5 leading-none`}
              style={{ zIndex: triggerZIndex }}
              // セル側の onPointerDown が編集モードに遷移させるため、ここで伝播を止める
              onPointerDown={(event) => {
                event.stopPropagation()
              }}
              onContextMenu={(event) => {
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.stopPropagation()
                onOpenChange(!open)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  event.stopPropagation()
                  onOpenChange(!open)
                }
              }}
            >
              <ExpandAltOutlined className="pointer-events-none text-[10px] text-gray-500" />
            </div>
          </Tooltip>
        ) : null}
      </div>
    </Popover>
  )
}
